/**
 * What the reformat did, recorded in the output's `DerivationDescription`
 * (0008,2111).
 *
 * The padding clause is not decoration. A reformat whose field of view
 * reaches past the source writes voxels no scanner produced, and this
 * attribute is the only place that survives export into a TPS to say so —
 * the panel warning reaches whoever ran it, this reaches whoever opens the
 * series afterwards.
 *
 * English, not localized: DICOM attribute values travel with the object to
 * systems that know nothing of the operator's UI language.
 */
export interface ReformatProvenance {
    /** Fraction of the output that is padding rather than resampled data. */
    paddedFraction: number;
    /**
     * Value written into the padded region, in the image's own units — the
     * modality-unit minimum of the source, not its stored value. The
     * writer reverses the rescale before it lands in PixelData.
     */
    paddingValue: number;
    /**
     * The extension, when there was one. Grouped so a slice count cannot
     * arrive without the margin it spans.
     */
    replication?: {
        /** Output slices that are copies of a boundary slice, both ends. */
        slices: number;
        /** Margin those slices span at EACH extended end, mm. */
        marginMm: number;
        /**
         * Measured image data the output leaves out because the extension
         * began inside the acquired range, mm along Z. Recorded because
         * the operator chose replicas over data that existed.
         */
        excludedMm: number;
    };
    /**
     * Extent of the source filled by duplicating a neighbouring slice
     * because no acquired slice lay near enough, mm.
     *
     * Recorded for the same reason the scatter replicas are: it is
     * anatomy the patient was not scanned for, and the series this tool
     * exists for are exactly the ones that carry gaps. In millimetres
     * because the count belongs to the assembled source's grid, not to
     * the output's.
     */
    gapFilledMm?: number;
}

/** The sentence every reformatted series carries. */
export const REFORMAT_DERIVATION_BASE =
    'Resampled to neutral axial HFS orientation (derived volume)';

/**
 * Rounded up, so a padded output is never described as unpadded: any
 * padding at all reads as at least 1%. Exported so the proportion the
 * operator accepts and the proportion the object records cannot drift.
 */
export function paddedPercent(fraction: number): number {
    return Math.ceil(fraction * 100);
}

/**
 * Millimetres as they should appear in a DICOM attribute. A margin is a
 * slice count times a thickness, so binary floating point turns 21 x 1.7 mm
 * into 35.699999999999996 — which is what a reader would then see.
 */
function mm(value: number): string {
    return `${Math.round(value * 10) / 10}`;
}

export function reformatDerivationDescription(
    provenance: ReformatProvenance
): string {
    const clauses = [REFORMAT_DERIVATION_BASE];

    if (provenance.paddedFraction > 0) {
        clauses.push(
            `${paddedPercent(provenance.paddedFraction)}% padded ` +
                `at ${provenance.paddingValue} (source minimum)`
        );
    }

    const replication = provenance.replication;
    if (replication && replication.slices > 0) {
        // Slice count is the total across both ends while the margin is
        // per end, so both are named rather than left to be read as one
        // span.
        clauses.push(
            `${replication.slices} ${replication.slices === 1 ? 'slice' : 'slices'} ` +
                `replicated from the boundary slice for scatter, ` +
                `${mm(replication.marginMm)} mm at each extended end ` +
                `(not acquired anatomy)`
        );

        if (replication.excludedMm > 0) {
            clauses.push(
                `${mm(replication.excludedMm)} mm of available image data ` +
                    `excluded`
            );
        }
    }

    if (provenance.gapFilledMm && provenance.gapFilledMm > 0) {
        clauses.push(
            `${mm(provenance.gapFilledMm)} mm of gaps in the source filled ` +
                `by duplicating neighbouring slices (not acquired anatomy)`
        );
    }

    return clauses.join('. ');
}
